/**
 * Validate Interaction System
 *
 * Manages the validation battery panel in Validate mode.
 *
 * When the field is in Validate mode and a user clicks a cluster,
 * this system looks up the cluster's validation battery and renders
 * it in the epistemology panel on the left side.
 *
 * The panel shows the 10 falsification and coherence tests with
 * their categorical states. No scores. No numbers. States only.
 *
 * "Is this real, defensible, and safe to act on?"
 */

import * as THREE from 'three';
import { useAuraStore } from '../state/store';
import type { SyntheticField, SyntheticNode } from '../data/synthetic';
import {
  generateValidationBatteries,
  countStateClasses,
  DIMENSION_LABELS,
  DIMENSION_DESCRIPTIONS,
  STATE_DISPLAY,
  STATE_CLASSES,
  type ClusterValidation,
  type ValidationBattery,
  type ValidationEvidence,
  type ConvergenceEvidence,
  type PersistenceEvidence,
  type GeographicEvidence,
  type SemanticEvidence,
  type NoiseEvidence,
  type IntentEvidence,
  type CausalEvidence,
  type EthicsEvidence,
  type FalsePositiveEvidence,
} from '../data/validation';
import type { FieldCamera } from '../scene/camera';
import type { NodeRenderer } from '../scene/nodes';
import { attachTooltip, hideTooltip } from './tooltip';
import {
  loadCustomModel,
  saveCustomModel,
  createDimension,
  addDimension,
  removeDimension,
  setAssignment,
  updateNotes,
  cycleState,
  getAssignedState,
  stateIdFromLabel,
  type CustomValidationModel,
  type CustomDimension,
  type CustomState,
  type CustomAssignment,
} from '../data/custom-validation';

// ─── TOOLTIP CONTENT ──────────────────────────────────────────
//
// Every state gets a plain-language explanation.
// These surface on hover so non-technical users understand
// what each categorical state actually means.

const STATE_TOOLTIPS: Record<string, string> = {
  // Convergence
  'convergent': 'Multiple independent signal classes confirm this trend',
  'single-source': 'Only one signal class detected — needs corroboration',
  'observational': 'Present in passive data but not in active search',
  // Persistence
  'persistent': 'Signal survives across multiple rolling time windows',
  'volatile': 'Signal is unstable — appears and disappears',
  'peaking': 'At maximum strength right now but may not sustain',
  'decaying': 'Signal strength is declining over time',
  // Geographic
  'diffusing': 'Spreading organically across geographic regions',
  'contained': 'Concentrated in a single region with no spread',
  'fragmented': 'Scattered and inconsistent across regions',
  'region-specific': 'Strong in one region, absent elsewhere',
  // Semantic
  'stable': 'Meaning has remained consistent over time',
  'evolving': 'Meaning is shifting but still coherent',
  'fracturing': 'Meaning is splitting into contradictory interpretations',
  // Noise
  'structured': 'Genuine, specific interest with high signal quality',
  'noisy': 'Hard to separate true signal from background chatter',
  'speculative': 'Interest is exploratory and non-committal',
  // Intent
  'low-commitment': 'Casual, shallow engagement — browsing only',
  'decision-forming': 'Active comparison and evaluation behavior',
  'identity-anchored': 'Deep, identity-level engagement and loyalty',
  // Causal
  'leading': 'This signal precedes and may predict related movement',
  'concurrent': 'Moving in parallel with related market signals',
  'reactive': 'Follows other signals — it does not lead',
  // Ethics
  'safe-to-activate': 'Data is ethical, consensual, and actionable',
  'observe-only': 'Monitor this signal but do not act on it yet',
  'restricted': 'Ethical or consent concerns — do not activate',
  // False positive
  'robust': 'Survived falsification and historical analog testing',
  'unproven': 'Cannot yet be falsified — needs more comparison data',
  'likely-false-positive': 'Matches historical false positive patterns',
  // Readiness
  'too-early': 'Insufficient evidence to inform decisions',
  'forming': 'Evidence is building but not yet defensible',
  'defensible': 'Rigorous enough to present to stakeholders',
  'ready-to-design-against': 'Full confidence — act on this signal',
};

/** Tooltip text for the S / F / W class selector buttons in the builder */
const CLASS_TOOLTIPS: Record<string, string> = {
  strong: 'Strong — signal passed this test',
  forming: 'Forming — developing, not yet confirmed',
  weak: 'Weak — signal failed or is insufficient',
};

export class ValidateInteraction {
  private panel: HTMLElement;
  private clusterNameEl: HTMLElement;
  private categoryEl: HTMLElement;
  private readinessEl: HTMLElement;
  private readinessStateEl: HTMLElement;
  private testsEl: HTMLElement;
  private summaryEl: HTMLElement;
  private emptyEl: HTMLElement;

  private validations: Map<string, ClusterValidation>;
  private raycaster = new THREE.Raycaster();
  private mouse = new THREE.Vector2();
  private mainClusters: SyntheticNode[] = [];

  private currentClusterId: string | null = null;
  private panelVisible = false;
  private panelTarget = 0;  // 0 = hidden, 1 = visible

  // Custom validation layer
  private customModel: CustomValidationModel;
  private customLayerEl: HTMLElement;
  private builderFormOpen = false;

  private handleClick: (e: MouseEvent) => void;

  constructor(
    private container: HTMLElement,
    private camera: THREE.PerspectiveCamera,
    private fieldCamera: FieldCamera,
    private nodeRenderer: NodeRenderer,
    private field: SyntheticField,
  ) {
    // Generate validation batteries
    this.validations = generateValidationBatteries();

    // Extract main clusters
    this.mainClusters = field.nodes.filter(n => n.id.startsWith('cluster-'));

    // DOM references
    this.panel = document.getElementById('validation-panel')!;
    this.clusterNameEl = document.getElementById('validation-cluster-name')!;
    this.categoryEl = document.getElementById('validation-cluster-category')!;
    this.readinessEl = document.getElementById('validation-readiness')!;
    this.readinessStateEl = document.getElementById('validation-readiness-state')!;
    this.testsEl = document.getElementById('validation-tests')!;
    this.summaryEl = document.getElementById('validation-summary')!;
    this.emptyEl = document.getElementById('validation-empty')!;

    // Custom validation model (customer-authored layer)
    this.customModel = loadCustomModel();
    this.customLayerEl = document.createElement('div');

    // Attach tooltips to static panel elements
    const panelHeader = this.panel.querySelector('.validation-header');
    if (panelHeader) {
      attachTooltip(panelHeader as HTMLElement, 'Is this real, defensible, and safe to act on?');
    }
    const sectionTitle = this.panel.querySelector('.validation-section-title');
    if (sectionTitle) {
      attachTooltip(sectionTitle as HTMLElement, '9 independent epistemological tests applied to each cluster');
    }

    // Click handler for cluster selection in Validate mode
    this.handleClick = (e: MouseEvent) => this.onClick(e);
    container.addEventListener('click', this.handleClick);
  }

  private onClick(e: MouseEvent) {
    const { mode } = useAuraStore.getState();
    if (mode !== 'validate') return;

    // Convert screen coordinates to normalized device coordinates
    const rect = this.container.getBoundingClientRect();
    this.mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

    // Find nearest main cluster to click ray
    const clickPos = new THREE.Vector3(this.mouse.x, this.mouse.y, 0.5);
    clickPos.unproject(this.camera);
    const rayDir = clickPos.sub(this.camera.position).normalize();
    const cameraPos = this.camera.position;

    let bestCluster: SyntheticNode | null = null;
    let bestDist = 60; // Max click radius in world units

    for (const cluster of this.mainClusters) {
      const clusterPos = new THREE.Vector3(...cluster.position);

      // Distance from point to ray
      const toCluster = clusterPos.clone().sub(cameraPos);
      const projDist = toCluster.dot(rayDir);
      if (projDist < 0) continue; // Behind camera

      const closestOnRay = cameraPos.clone().add(rayDir.clone().multiplyScalar(projDist));
      const perpDist = closestOnRay.distanceTo(clusterPos);

      // Scale threshold by cluster weight (bigger clusters have bigger hit targets)
      const threshold = 15 + cluster.intensity * 45;

      if (perpDist < threshold && perpDist < bestDist) {
        bestDist = perpDist;
        bestCluster = cluster;
      }
    }

    if (bestCluster) {
      this.showValidation(bestCluster);
    }
  }

  private showValidation(cluster: SyntheticNode) {
    const validation = this.validations.get(cluster.id);
    if (!validation) return;

    this.currentClusterId = cluster.id;

    // Populate header
    this.clusterNameEl.textContent = validation.clusterName;
    this.categoryEl.textContent = validation.category;

    // Readiness state (the synthesis — goes at top)
    const readinessClass = STATE_CLASSES[validation.battery.readiness];
    this.readinessEl.className = `validation-readiness ${readinessClass}`;
    this.readinessStateEl.textContent = STATE_DISPLAY[validation.battery.readiness];

    // Tooltip on readiness: dimension description + state explanation
    const readinessTip = `${DIMENSION_DESCRIPTIONS.readiness}. Current: ${STATE_TOOLTIPS[validation.battery.readiness] || ''}`;
    attachTooltip(this.readinessEl, readinessTip);

    // Dismiss any stale tooltip before re-rendering
    hideTooltip();

    // Build the 9-test battery display (readiness is shown separately at top)
    this.testsEl.innerHTML = '';

    const dimensions: (keyof ValidationBattery)[] = [
      'convergence', 'persistence', 'geographic', 'semantic',
      'noise', 'intent', 'causal', 'ethics', 'falsePositive',
    ];

    for (const dim of dimensions) {
      const state = validation.battery[dim];
      const stateClass = STATE_CLASSES[state];

      // Outer container — becomes expandable
      const test = document.createElement('div');
      test.className = 'validation-test';

      // Clickable header row: chevron + label + state badge
      const headerRow = document.createElement('div');
      headerRow.className = 'validation-test-row';

      const left = document.createElement('div');
      left.className = 'validation-test-left';

      const chevron = document.createElement('div');
      chevron.className = 'validation-test-chevron';
      chevron.textContent = '▶';

      const label = document.createElement('div');
      label.className = 'validation-test-label';
      label.textContent = DIMENSION_LABELS[dim];

      // Tooltip: what this dimension tests
      attachTooltip(label, DIMENSION_DESCRIPTIONS[dim]);

      left.appendChild(chevron);
      left.appendChild(label);

      const badge = document.createElement('div');
      badge.className = `validation-test-state ${stateClass}`;
      badge.textContent = STATE_DISPLAY[state];

      // Tooltip: what this state means
      if (STATE_TOOLTIPS[state]) {
        attachTooltip(badge, STATE_TOOLTIPS[state]);
      }

      headerRow.appendChild(left);
      headerRow.appendChild(badge);

      // Evidence container (hidden until expanded)
      const evidenceContainer = document.createElement('div');
      evidenceContainer.className = 'validation-evidence';

      const evidenceInner = document.createElement('div');
      evidenceInner.className = 'validation-evidence-inner';

      // Render dimension-specific evidence
      this.renderEvidence(dim, validation.evidence, evidenceInner);

      evidenceContainer.appendChild(evidenceInner);

      // Assemble
      test.appendChild(headerRow);
      test.appendChild(evidenceContainer);
      this.testsEl.appendChild(test);

      // Click to expand/collapse
      headerRow.addEventListener('click', (e: MouseEvent) => {
        e.stopPropagation();
        test.classList.toggle('expanded');
      });
    }

    // Summary counts
    const counts = countStateClasses(validation.battery);
    this.summaryEl.innerHTML = '';

    const summaryTips: Record<string, string> = {
      strong: 'Tests where the signal passed — evidence is solid',
      forming: 'Tests where evidence is developing but not confirmed',
      weak: 'Tests where the signal failed or evidence is insufficient',
    };

    const makeCount = (cls: string, count: number) => {
      const item = document.createElement('div');
      item.className = 'validation-summary-item';
      item.innerHTML = `
        <div class="validation-summary-dot ${cls}"></div>
        <div class="validation-summary-count">${count} ${cls}</div>
      `;
      attachTooltip(item, summaryTips[cls] || '');
      return item;
    };

    if (counts.strong > 0) this.summaryEl.appendChild(makeCount('strong', counts.strong));
    if (counts.forming > 0) this.summaryEl.appendChild(makeCount('forming', counts.forming));
    if (counts.weak > 0) this.summaryEl.appendChild(makeCount('weak', counts.weak));

    // Show tests, hide empty state
    this.emptyEl.style.display = 'none';
    this.testsEl.style.display = 'block';
    this.readinessEl.style.display = 'flex';
    this.summaryEl.style.display = 'flex';
    this.clusterNameEl.style.display = 'block';
    this.categoryEl.style.display = 'block';

    // Custom validation layer (customer-authored tests below core battery)
    this.renderCustomLayer();
    this.testsEl.appendChild(this.customLayerEl);

    this.panelTarget = 1;
  }

  // ─── EVIDENCE RENDERING ──────────────────────────────────────
  //
  // Each dimension gets its own visual language.
  // No dimension is rendered the same way.
  // The rendering matches the epistemological nature of the test.

  private renderEvidence(
    dim: keyof ValidationBattery,
    evidence: ValidationEvidence,
    container: HTMLElement,
  ) {
    switch (dim) {
      case 'convergence': this.renderConvergence(evidence.convergence, container); break;
      case 'persistence': this.renderPersistence(evidence.persistence, container); break;
      case 'geographic':  this.renderGeographic(evidence.geographic, container); break;
      case 'semantic':    this.renderSemantic(evidence.semantic, container); break;
      case 'noise':       this.renderNoise(evidence.noise, container); break;
      case 'intent':      this.renderIntent(evidence.intent, container); break;
      case 'causal':      this.renderCausal(evidence.causal, container); break;
      case 'ethics':      this.renderEthics(evidence.ethics, container); break;
      case 'falsePositive': this.renderFalsePositive(evidence.falsePositive, container); break;
    }
  }

  /** Signal Convergence — shows which independent signal classes detected the trend */
  private renderConvergence(ev: ConvergenceEvidence, container: HTMLElement) {
    // Reasoning paragraph first
    container.appendChild(this.makeReasoning(ev.reasoning));

    // Section label
    container.appendChild(this.makeSectionLabel('Signal Classes'));

    // Source cards
    for (const source of ev.sources) {
      const card = document.createElement('div');
      card.className = 'evidence-source';

      const indicator = document.createElement('div');
      indicator.className = `evidence-source-indicator ${source.detected ? 'detected' : 'absent'}`;

      const info = document.createElement('div');
      info.className = 'evidence-source-info';

      const typeLabel = document.createElement('div');
      typeLabel.className = 'evidence-source-type';
      typeLabel.textContent = source.label;

      const detail = document.createElement('div');
      detail.className = 'evidence-source-detail';
      detail.textContent = source.detail;

      info.appendChild(typeLabel);
      info.appendChild(detail);
      card.appendChild(indicator);
      card.appendChild(info);
      container.appendChild(card);
    }
  }

  /** Temporal Persistence — shows signal presence across time windows */
  private renderPersistence(ev: PersistenceEvidence, container: HTMLElement) {
    container.appendChild(this.makeReasoning(ev.reasoning));
    container.appendChild(this.makeSectionLabel('Time Windows'));

    for (const window of ev.windows) {
      const row = document.createElement('div');
      row.className = 'evidence-window';

      const label = document.createElement('div');
      label.className = 'evidence-window-label';
      label.textContent = window.label;

      const bar = document.createElement('div');
      bar.className = 'evidence-window-bar';

      const fill = document.createElement('div');
      fill.className = `evidence-window-fill ${window.state}`;

      bar.appendChild(fill);

      const stateLabel = document.createElement('div');
      stateLabel.className = `evidence-window-state ${window.state}`;
      stateLabel.textContent = window.state.charAt(0).toUpperCase() + window.state.slice(1);

      row.appendChild(label);
      row.appendChild(bar);
      row.appendChild(stateLabel);
      container.appendChild(row);
    }

    // Pattern summary
    const pattern = document.createElement('div');
    pattern.className = 'evidence-text';
    pattern.textContent = ev.pattern;
    container.appendChild(pattern);
  }

  /** Cross-Geographic Coherence — region signals with lag indicators */
  private renderGeographic(ev: GeographicEvidence, container: HTMLElement) {
    container.appendChild(this.makeReasoning(ev.reasoning));
    container.appendChild(this.makeSectionLabel('Regional Signals'));

    for (const region of ev.regions) {
      const row = document.createElement('div');
      row.className = 'evidence-region';

      const dot = document.createElement('div');
      dot.className = `evidence-region-dot ${region.state}`;

      const name = document.createElement('div');
      name.className = 'evidence-region-name';
      name.textContent = region.region;

      row.appendChild(dot);
      row.appendChild(name);

      if (region.lag) {
        const lag = document.createElement('div');
        lag.className = 'evidence-region-lag';
        lag.textContent = region.lag;
        row.appendChild(lag);
      }

      container.appendChild(row);
    }

    // Diffusion pattern
    const pattern = document.createElement('div');
    pattern.className = 'evidence-text';
    pattern.textContent = ev.diffusionPattern;
    container.appendChild(pattern);
  }

  /** Semantic Stability — meaning cluster distributions */
  private renderSemantic(ev: SemanticEvidence, container: HTMLElement) {
    container.appendChild(this.makeReasoning(ev.reasoning));

    // Centroid drift datum
    container.appendChild(this.makeDatum('Centroid Drift', ev.centroidDrift));
    container.appendChild(this.makeSectionLabel('Meaning Clusters'));

    for (const meaning of ev.meanings) {
      const row = document.createElement('div');
      row.className = 'evidence-meaning';

      const label = document.createElement('div');
      label.className = 'evidence-meaning-label';
      label.textContent = meaning.label;

      const bar = document.createElement('div');
      bar.className = 'evidence-meaning-bar';

      const fill = document.createElement('div');
      fill.className = 'evidence-meaning-fill';
      fill.style.width = meaning.share;

      bar.appendChild(fill);

      const share = document.createElement('div');
      share.className = 'evidence-meaning-share';
      share.textContent = meaning.share;

      row.appendChild(label);
      row.appendChild(bar);
      row.appendChild(share);
      container.appendChild(row);
    }
  }

  /** Noise-to-Signal — query specificity metrics */
  private renderNoise(ev: NoiseEvidence, container: HTMLElement) {
    container.appendChild(this.makeReasoning(ev.reasoning));
    container.appendChild(this.makeSectionLabel('Signal Quality'));
    container.appendChild(this.makeDatum('Query Specificity', ev.querySpecificity));
    container.appendChild(this.makeDatum('Long-tail Ratio', ev.longTailRatio));
    container.appendChild(this.makeDatum('Repeat Intent', ev.repeatIntent));
  }

  /** Intent Depth — breakdown bars by intent type */
  private renderIntent(ev: IntentEvidence, container: HTMLElement) {
    container.appendChild(this.makeReasoning(ev.reasoning));
    container.appendChild(this.makeSectionLabel('Intent Breakdown'));

    for (const item of ev.breakdown) {
      const row = document.createElement('div');
      row.className = 'evidence-intent';

      const typeLabel = document.createElement('div');
      typeLabel.className = 'evidence-intent-type';
      typeLabel.textContent = item.label;

      const bar = document.createElement('div');
      bar.className = 'evidence-intent-bar';

      const fill = document.createElement('div');
      fill.className = `evidence-intent-fill ${item.type}`;
      fill.style.width = item.share;

      bar.appendChild(fill);

      const share = document.createElement('div');
      share.className = 'evidence-intent-share';
      share.textContent = item.share;

      row.appendChild(typeLabel);
      row.appendChild(bar);
      row.appendChild(share);
      container.appendChild(row);
    }

    // Dominant signal
    const dominant = document.createElement('div');
    dominant.className = 'evidence-text';
    dominant.textContent = ev.dominantSignal;
    container.appendChild(dominant);
  }

  /** Causal Precursor — position statement and precursor chain */
  private renderCausal(ev: CausalEvidence, container: HTMLElement) {
    container.appendChild(this.makeReasoning(ev.reasoning));
    container.appendChild(this.makeSectionLabel('Temporal Position'));

    const position = document.createElement('div');
    position.className = 'evidence-text';
    position.textContent = ev.position;
    container.appendChild(position);

    container.appendChild(this.makeSectionLabel('Precursors'));

    for (const precursor of ev.precursors) {
      const text = document.createElement('div');
      text.className = 'evidence-text';
      text.textContent = `→ ${precursor}`;
      container.appendChild(text);
    }
  }

  /** Consent & Ethical Safety — consent class, domains, restrictions */
  private renderEthics(ev: EthicsEvidence, container: HTMLElement) {
    container.appendChild(this.makeReasoning(ev.reasoning));
    container.appendChild(this.makeSectionLabel('Data Provenance'));
    container.appendChild(this.makeDatum('Consent Class', ev.consentClass));

    if (ev.sensitiveDomains.length > 0) {
      container.appendChild(this.makeSectionLabel('Sensitive Domains'));
      for (const domain of ev.sensitiveDomains) {
        const text = document.createElement('div');
        text.className = 'evidence-text';
        text.textContent = `· ${domain}`;
        container.appendChild(text);
      }
    }

    if (ev.restrictions.length > 0) {
      container.appendChild(this.makeSectionLabel('Restrictions'));
      for (const restriction of ev.restrictions) {
        const el = document.createElement('div');
        el.className = 'evidence-restriction';
        el.textContent = restriction;
        container.appendChild(el);
      }
    } else {
      const safe = document.createElement('div');
      safe.className = 'evidence-safe';
      safe.textContent = 'No restrictions. Safe for general activation.';
      container.appendChild(safe);
    }
  }

  /** False Positive Stress Test — historical analogs and null hypothesis */
  private renderFalsePositive(ev: FalsePositiveEvidence, container: HTMLElement) {
    container.appendChild(this.makeReasoning(ev.reasoning));
    container.appendChild(this.makeSectionLabel('Historical Analogs'));

    for (const analog of ev.historicalAnalogs) {
      const row = document.createElement('div');
      row.className = 'evidence-analog';

      const dot = document.createElement('div');
      dot.className = `evidence-analog-dot ${analog.outcome}`;

      const name = document.createElement('div');
      name.className = 'evidence-analog-name';
      name.textContent = analog.name;

      const outcome = document.createElement('div');
      outcome.className = `evidence-analog-outcome ${analog.outcome}`;
      outcome.textContent = analog.outcome;

      const year = document.createElement('div');
      year.className = 'evidence-analog-year';
      year.textContent = analog.year;

      row.appendChild(dot);
      row.appendChild(name);
      row.appendChild(outcome);
      row.appendChild(year);
      container.appendChild(row);
    }

    // Seasonality check
    container.appendChild(this.makeSectionLabel('Stress Tests'));
    container.appendChild(this.makeDatum('Seasonality', ev.seasonalityCheck));

    // Null hypothesis
    const nullH = document.createElement('div');
    nullH.className = 'evidence-text';
    nullH.textContent = ev.nullHypothesis;
    container.appendChild(nullH);
  }

  // ─── EVIDENCE HELPERS ────────────────────────────────────────

  private makeReasoning(text: string): HTMLElement {
    const el = document.createElement('div');
    el.className = 'evidence-reasoning';
    el.textContent = text;
    return el;
  }

  private makeSectionLabel(text: string): HTMLElement {
    const el = document.createElement('div');
    el.className = 'evidence-section-label';
    el.textContent = text;
    return el;
  }

  private makeDatum(label: string, value: string): HTMLElement {
    const row = document.createElement('div');
    row.className = 'evidence-datum';

    const labelEl = document.createElement('div');
    labelEl.className = 'evidence-datum-label';
    labelEl.textContent = label;

    const valueEl = document.createElement('div');
    valueEl.className = 'evidence-datum-value';
    valueEl.textContent = value;

    row.appendChild(labelEl);
    row.appendChild(valueEl);
    return row;
  }

  // ─── CUSTOM VALIDATION LAYER ──────────────────────────────────
  //
  // The customer's lens. Domain-specific tests they author themselves.
  // Core 10 are immutable. These sit below.

  private renderCustomLayer() {
    this.customLayerEl.innerHTML = '';
    this.builderFormOpen = false;

    // Section header
    const header = document.createElement('div');
    header.className = 'custom-section-header';

    const title = document.createElement('div');
    title.className = 'custom-section-title';
    title.textContent = 'Your Tests';

    const addBtn = document.createElement('button');
    addBtn.className = 'custom-add-btn';
    addBtn.textContent = '+ Add';
    addBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (!this.builderFormOpen) {
        this.showBuilderForm();
      }
    });

    // Tooltips for custom section
    attachTooltip(title, 'Your domain-specific validation lens, layered on top of the core battery');
    attachTooltip(addBtn, 'Create a new custom validation test');

    header.appendChild(title);
    header.appendChild(addBtn);
    this.customLayerEl.appendChild(header);

    // Show dimensions or empty prompt
    if (this.customModel.dimensions.length === 0 && !this.builderFormOpen) {
      const prompt = document.createElement('div');
      prompt.className = 'custom-empty-prompt';
      prompt.textContent = 'Add your own validation lens';
      this.customLayerEl.appendChild(prompt);
    } else {
      for (const dimension of this.customModel.dimensions) {
        this.renderCustomTest(dimension, this.customLayerEl);
      }
    }
  }

  private renderCustomTest(dimension: CustomDimension, container: HTMLElement) {
    if (!this.currentClusterId) return;

    const test = document.createElement('div');
    test.className = 'custom-test';

    // Header row: chevron + name + state badge + actions
    const headerRow = document.createElement('div');
    headerRow.className = 'custom-test-row';

    const left = document.createElement('div');
    left.className = 'custom-test-left';

    const chevron = document.createElement('div');
    chevron.className = 'custom-test-chevron';
    chevron.textContent = '▶';

    const name = document.createElement('div');
    name.className = 'custom-test-name';
    name.textContent = dimension.name;

    // Tooltip on custom test name: show its description
    if (dimension.description) {
      attachTooltip(name, dimension.description);
    }

    left.appendChild(chevron);
    left.appendChild(name);

    const right = document.createElement('div');
    right.className = 'custom-test-right';

    // State badge (clickable to cycle through states)
    const assignedState = getAssignedState(dimension, this.currentClusterId);
    const stateBadge = document.createElement('div');

    if (assignedState) {
      stateBadge.className = `custom-test-state ${assignedState.stateClass}`;
      stateBadge.textContent = assignedState.label;
      attachTooltip(stateBadge, 'Click to cycle to the next state');
    } else {
      stateBadge.className = 'custom-test-state unset';
      stateBadge.textContent = 'Set state';
      attachTooltip(stateBadge, 'Click to assign a state for this cluster');
    }

    stateBadge.addEventListener('click', (e) => {
      e.stopPropagation();
      this.onCycleState(dimension);
    });

    // Edit / delete actions
    const actions = document.createElement('div');
    actions.className = 'custom-test-actions';

    const deleteBtn = document.createElement('button');
    deleteBtn.className = 'custom-test-action delete';
    deleteBtn.textContent = '×';
    attachTooltip(deleteBtn, 'Remove this test');
    deleteBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.onDeleteDimension(dimension.id);
    });

    actions.appendChild(deleteBtn);

    right.appendChild(stateBadge);
    right.appendChild(actions);

    headerRow.appendChild(left);
    headerRow.appendChild(right);

    // Expandable evidence: description + notes textarea
    const evidenceContainer = document.createElement('div');
    evidenceContainer.className = 'custom-evidence';

    const evidenceInner = document.createElement('div');
    evidenceInner.className = 'custom-evidence-inner';

    // Dimension description
    const desc = document.createElement('div');
    desc.className = 'custom-description';
    desc.textContent = dimension.description;
    evidenceInner.appendChild(desc);

    // Notes textarea
    const notesLabel = document.createElement('div');
    notesLabel.className = 'evidence-section-label';
    notesLabel.textContent = 'Your Reasoning';

    const assignment = dimension.assignments[this.currentClusterId];
    const notes = document.createElement('textarea');
    notes.className = 'custom-notes';
    notes.placeholder = 'Why did you assign this state for this cluster?';
    notes.value = assignment?.notes ?? '';

    // Debounced save on input
    let saveTimeout: ReturnType<typeof setTimeout>;
    notes.addEventListener('input', () => {
      clearTimeout(saveTimeout);
      saveTimeout = setTimeout(() => {
        if (!this.currentClusterId) return;
        updateNotes(this.customModel, dimension.id, this.currentClusterId, notes.value);
      }, 400);
    });

    // Prevent click-through to canvas
    notes.addEventListener('click', (e) => e.stopPropagation());

    evidenceInner.appendChild(notesLabel);
    evidenceInner.appendChild(notes);
    evidenceContainer.appendChild(evidenceInner);

    // Assemble
    test.appendChild(headerRow);
    test.appendChild(evidenceContainer);
    container.appendChild(test);

    // Click header to expand/collapse
    headerRow.addEventListener('click', (e: MouseEvent) => {
      e.stopPropagation();
      test.classList.toggle('expanded');
    });
  }

  private onCycleState(dimension: CustomDimension) {
    if (!this.currentClusterId) return;

    const current = dimension.assignments[this.currentClusterId];
    const nextState = cycleState(dimension, current?.stateId);
    if (!nextState) return;

    setAssignment(
      this.customModel,
      dimension.id,
      this.currentClusterId,
      nextState.id,
      current?.notes,
    );

    // Re-render custom layer (preserves core tests)
    this.renderCustomLayer();
    this.testsEl.appendChild(this.customLayerEl);
  }

  private onDeleteDimension(dimensionId: string) {
    removeDimension(this.customModel, dimensionId);
    this.renderCustomLayer();
    this.testsEl.appendChild(this.customLayerEl);
  }

  // ─── BUILDER FORM ─────────────────────────────────────────────

  private showBuilderForm() {
    this.builderFormOpen = true;

    const form = document.createElement('div');
    form.className = 'custom-builder-form';
    form.id = 'custom-builder-form';

    // Prevent form clicks from propagating to canvas
    form.addEventListener('click', (e) => e.stopPropagation());

    // Name input
    const nameSection = document.createElement('div');
    nameSection.className = 'custom-builder-section';

    const nameLabel = document.createElement('div');
    nameLabel.className = 'custom-builder-label';
    nameLabel.textContent = 'Test Name';
    attachTooltip(nameLabel, 'Give your validation test a short, descriptive name');

    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.className = 'custom-builder-input';
    nameInput.placeholder = 'e.g., Brand Alignment';

    nameSection.appendChild(nameLabel);
    nameSection.appendChild(nameInput);
    form.appendChild(nameSection);

    // Description
    const descSection = document.createElement('div');
    descSection.className = 'custom-builder-section';

    const descLabel = document.createElement('div');
    descLabel.className = 'custom-builder-label';
    descLabel.textContent = 'What does this test measure?';
    attachTooltip(descLabel, 'Describe when and why this signal matters to your work');

    const descInput = document.createElement('textarea');
    descInput.className = 'custom-builder-textarea';
    descInput.placeholder = 'Describe when this signal matters to your work';

    descSection.appendChild(descLabel);
    descSection.appendChild(descInput);
    form.appendChild(descSection);

    // State definitions
    const statesSection = document.createElement('div');
    statesSection.className = 'custom-builder-section';

    const statesLabel = document.createElement('div');
    statesLabel.className = 'custom-builder-label';
    statesLabel.textContent = 'Define States';
    attachTooltip(statesLabel, 'Create 2–4 categorical states. Each gets a class: Strong, Forming, or Weak.');

    statesSection.appendChild(statesLabel);

    const statesGrid = document.createElement('div');
    statesGrid.className = 'custom-states-grid';

    // Default 3 state rows
    this.addStateRow(statesGrid, 'strong');
    this.addStateRow(statesGrid, 'forming');
    this.addStateRow(statesGrid, 'weak');

    statesSection.appendChild(statesGrid);

    // Add state button
    const addStateBtn = document.createElement('button');
    addStateBtn.className = 'custom-builder-add-state';
    addStateBtn.textContent = '+ another state';
    addStateBtn.addEventListener('click', (e) => {
      e.preventDefault();
      const rows = statesGrid.querySelectorAll('.custom-state-row');
      if (rows.length < 4) {
        this.addStateRow(statesGrid, 'forming');
      }
    });

    statesSection.appendChild(addStateBtn);
    form.appendChild(statesSection);

    // Buttons: Save / Cancel
    const buttons = document.createElement('div');
    buttons.className = 'custom-builder-buttons';

    const saveBtn = document.createElement('button');
    saveBtn.className = 'custom-builder-btn save';
    saveBtn.textContent = 'Save Test';
    saveBtn.addEventListener('click', () => {
      this.onSaveDimension(nameInput, descInput, statesGrid);
    });

    const cancelBtn = document.createElement('button');
    cancelBtn.className = 'custom-builder-btn';
    cancelBtn.textContent = 'Cancel';
    cancelBtn.addEventListener('click', () => {
      this.hideBuilderForm();
    });

    buttons.appendChild(saveBtn);
    buttons.appendChild(cancelBtn);
    form.appendChild(buttons);

    // Insert form before the custom tests
    const header = this.customLayerEl.querySelector('.custom-section-header');
    if (header && header.nextSibling) {
      this.customLayerEl.insertBefore(form, header.nextSibling);
    } else {
      this.customLayerEl.appendChild(form);
    }

    // Focus name input
    requestAnimationFrame(() => nameInput.focus());
  }

  private addStateRow(container: HTMLElement, defaultClass: 'strong' | 'forming' | 'weak') {
    const row = document.createElement('div');
    row.className = 'custom-state-row';

    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'custom-state-input';
    input.placeholder = 'State name';

    const classSelector = document.createElement('div');
    classSelector.className = 'custom-state-class';

    const classes: Array<{ cls: 'strong' | 'forming' | 'weak'; letter: string }> = [
      { cls: 'strong', letter: 'S' },
      { cls: 'forming', letter: 'F' },
      { cls: 'weak', letter: 'W' },
    ];

    for (const { cls, letter } of classes) {
      const btn = document.createElement('button');
      btn.className = `custom-state-class-btn cls-${cls}${cls === defaultClass ? ' active' : ''}`;
      btn.textContent = letter;
      btn.dataset.cls = cls;
      attachTooltip(btn, CLASS_TOOLTIPS[cls]);
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        classSelector.querySelectorAll('.custom-state-class-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
      });
      classSelector.appendChild(btn);
    }

    const removeBtn = document.createElement('button');
    removeBtn.className = 'custom-state-remove';
    removeBtn.textContent = '×';
    removeBtn.addEventListener('click', (e) => {
      e.preventDefault();
      row.remove();
    });

    row.appendChild(input);
    row.appendChild(classSelector);
    row.appendChild(removeBtn);
    container.appendChild(row);
  }

  private onSaveDimension(
    nameInput: HTMLInputElement,
    descInput: HTMLTextAreaElement,
    statesGrid: HTMLElement,
  ) {
    const name = nameInput.value.trim();
    const description = descInput.value.trim();

    if (!name) {
      nameInput.style.borderColor = 'rgba(180,100,100,0.5)';
      nameInput.focus();
      return;
    }

    // Collect states from form rows
    const rows = Array.from(statesGrid.querySelectorAll('.custom-state-row'));
    const states: CustomState[] = [];

    for (const row of rows) {
      const input = row.querySelector('.custom-state-input') as HTMLInputElement;
      const activeBtn = row.querySelector('.custom-state-class-btn.active') as HTMLElement;

      const label = input.value.trim();
      if (!label) continue;

      const cls = (activeBtn?.dataset.cls ?? 'forming') as 'strong' | 'forming' | 'weak';
      states.push({
        id: stateIdFromLabel(label),
        label,
        stateClass: cls,
      });
    }

    if (states.length < 2) {
      // Need at least 2 states
      const firstInput = statesGrid.querySelector('.custom-state-input') as HTMLInputElement;
      if (firstInput) {
        firstInput.style.borderColor = 'rgba(180,100,100,0.5)';
        firstInput.focus();
      }
      return;
    }

    // Create and add the dimension
    const dim = createDimension(name, description || name, states);
    addDimension(this.customModel, dim);

    // Re-render
    this.hideBuilderForm();
  }

  private hideBuilderForm() {
    this.builderFormOpen = false;
    this.renderCustomLayer();
    this.testsEl.appendChild(this.customLayerEl);
  }

  private showEmpty() {
    this.emptyEl.style.display = 'block';
    this.testsEl.style.display = 'none';
    this.readinessEl.style.display = 'none';
    this.summaryEl.style.display = 'none';
    this.clusterNameEl.style.display = 'none';
    this.categoryEl.style.display = 'none';
    this.customLayerEl.innerHTML = '';
    this.currentClusterId = null;
  }

  update() {
    const { mode, modeTransitionProgress } = useAuraStore.getState();
    const inValidate = mode === 'validate';

    // Show panel only in validate mode
    if (inValidate && modeTransitionProgress > 0.3) {
      if (!this.panelVisible) {
        this.panelVisible = true;
        this.panel.classList.add('visible');
        // Show empty state if no cluster selected
        if (!this.currentClusterId) {
          this.showEmpty();
        }
      }
    } else {
      if (this.panelVisible) {
        this.panelVisible = false;
        this.panel.classList.remove('visible');
        hideTooltip();
      }
    }
  }

  dispose() {
    this.container.removeEventListener('click', this.handleClick);
  }
}
